import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../contexts/I18nContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { LanguagePicker } from '../components/LanguagePicker';
import { Loader2, AlertCircle } from 'lucide-react';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, user } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();

  const getRedirectPath = (u) => {
    if (u?.must_change_password) return '/change-password';
    if (u?.is_super_admin || u?.is_master_admin) return '/super-admin';
    if (u?.role === 'client') return '/my-dashboard';
    return '/dashboard';
  };

  useEffect(() => {
    if (user) {
      navigate(getRedirectPath(user));
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const userData = await login(email, password);
      navigate(getRedirectPath(userData));
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al iniciar sesion');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex relative">
      {/* Persistent language toggle in the corner so users can change it before/after login */}
      <div className="fixed top-4 right-4 z-30">
        <LanguagePicker />
      </div>
      {/* Left side - Image */}
      <div 
        className="hidden lg:flex lg:w-1/2 bg-cover bg-center relative"
        style={{ 
          backgroundImage: 'url(https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?q=80&w=2070&auto=format&fit=crop)' 
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-background via-background/80 to-transparent" />
        <div className="relative z-10 p-12 flex flex-col justify-end">
          <img 
            src="/logo-ofimex-white.png" 
            alt="OFIMEX" 
            className="h-16 w-auto object-contain mb-4"
          />
          <p className="text-lg text-zinc-300 max-w-md">
            Sistema de gestión de operaciones de importación. Control total de tus embarques y contenedores.
          </p>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-background">
        <div className="w-full max-w-md space-y-8">
          {/* Logo for mobile */}
          <div className="lg:hidden text-center">
            <div className="inline-flex items-center gap-3 mb-2">
              <img 
                src="/logo-ofimex-white.png" 
                alt="OFIMEX" 
                className="h-12 w-auto object-contain"
              />
            </div>
            <p className="text-muted-foreground text-sm">
              Sistema de Operaciones de Importación
            </p>
          </div>

          <div className="space-y-2">
            <h2 className="font-heading text-3xl">{t('auth.loginTitle')}</h2>
            <p className="text-muted-foreground">
              {t('auth.loginSubtitle')}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="flex items-center gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-sm text-destructive text-sm">
                <AlertCircle size={16} />
                {error}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="email">{t('auth.email')}</Label>
              <Input
                id="email"
                type="email"
                placeholder="correo@ejemplo.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="bg-card border-border"
                data-testid="login-email"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">{t('auth.password')}</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="bg-card border-border"
                data-testid="login-password"
              />
              <div className="flex justify-end">
                <Link 
                  to="/forgot-password" 
                  className="text-xs text-primary hover:underline"
                  data-testid="forgot-password-link"
                >
                  {t('auth.forgotPassword')}
                </Link>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={loading}
              data-testid="login-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Ingresando...
                </>
              ) : (
                t('auth.loginButton')
              )}
            </Button>
          </form>

          {/* Links adicionales */}
          <div className="space-y-3 pt-4 border-t border-border">
            <p className="text-center text-sm text-muted-foreground">
              ¿No tienes cuenta?{' '}
              <Link 
                to="/#contacto" 
                className="text-primary hover:underline font-medium"
                data-testid="request-account-link"
              >
                Solicitar acceso
              </Link>
            </p>
            <p className="text-center">
              <Link 
                to="/" 
                className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
                data-testid="back-to-home-link"
              >
                ← Volver al inicio
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
